import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileCheck, CheckCircle2, AlertTriangle, Upload } from 'lucide-react';

export const DocReadinessCard = ({ selectedYear, docReadiness, docNA, onToggleNA }) => {
  const navigate = useNavigate();
  return (
    <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-sky-500" data-testid="accountant-checklist-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-base">
          <FileCheck className="h-5 w-5 text-sky-400" />
          What Your Accountant Needs — {selectedYear}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-3">
          To complete your tax return, your accountant will need the documents below. Upload them via the Pay & Contracts section.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {docReadiness.map(item => {
            const isNA = !!docNA[item.key];
            const status = isNA ? 'na' : item.uploaded ? 'uploaded' : 'missing';
            return (
              <div key={item.key} className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors ${
                status === 'na'
                  ? 'bg-slate-500/5 border-slate-600/30'
                  : status === 'uploaded'
                    ? 'bg-emerald-500/5 border-emerald-500/20'
                    : 'bg-red-500/5 border-red-500/20'
              }`} data-testid={`doc-check-${item.key}`}>
                {status === 'na' ? (
                  <span className="text-slate-500 text-xs font-medium shrink-0 w-4 text-center">—</span>
                ) : status === 'uploaded' ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${
                    status === 'na' ? 'text-slate-500 line-through' : status === 'uploaded' ? 'text-emerald-300' : 'text-red-300'
                  }`}>
                    {item.label}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">{item.description}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <label className="flex items-center gap-1 cursor-pointer" title="Mark as not applicable"
                    data-testid={`na-checkbox-${item.key}`}>
                    <input
                      type="checkbox"
                      checked={isNA}
                      onChange={() => onToggleNA(item.key)}
                      className="rounded border-slate-600 bg-slate-700 text-slate-500 focus:ring-slate-500 h-3.5 w-3.5"
                    />
                    <span className="text-[10px] text-slate-500">N/A</span>
                  </label>
                  {status === 'missing' && (
                    <Button size="sm" variant="ghost" onClick={() => navigate('/earnings')}
                      className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-7 px-2 text-xs">
                      <Upload className="h-3 w-3 mr-1" />Upload
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {docReadiness.length > 0 && (
          <div className="mt-3 flex items-center gap-3 text-xs">
            <span className="text-emerald-400">{docReadiness.filter(d => d.uploaded && !docNA[d.key]).length} uploaded</span>
            <span className="text-slate-600">|</span>
            <span className="text-red-400">{docReadiness.filter(d => !d.uploaded && !docNA[d.key]).length} missing</span>
            {Object.values(docNA).filter(Boolean).length > 0 && (
              <>
                <span className="text-slate-600">|</span>
                <span className="text-slate-500">{Object.values(docNA).filter(Boolean).length} N/A</span>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
